// LeadStageColumn.js
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { BarChart2, User } from "lucide-react";
import LeadCard from "./LeadCard";
import "react-circular-progressbar/dist/styles.css";
import InnerLoader from "../../../../component/ui/loader/inner-loader";

// Skeleton for loading
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded-md ${className}`} />
);

export default function LeadStageColumn({
  stage,
  leads,
  onLeadClick,
  isLoading,
  colors,
  counts,
  isLast = false,
  cardLoad,
  onSuccess = () => {},
  onError = () => {},
  productionUsers = [],
  getUsers = () => {},
}) {
  const [showCircle, setShowCircle] = useState(false);

  const sortedLeads = useMemo(
    () => [...leads].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [leads]
  );

  const totalLeads = Object.values(counts || {}).reduce(
    (sum, val) => sum + (val || 0),
    0
  );

  const stageCount = counts?.[`${stage.id}`] ?? leads.length;

  const percent =
    totalLeads > 0 ? Math.round((stageCount / totalLeads) * 100) : 0;

  const getPercentTextClass = (p) => {
    if (p >= 100) return "text-xs";
    if (p >= 10) return "text-sm";
    return "text-base";
  };

  const [openDropDownId, setOpenDropDownId] = useState(null);

  const handleDropDownToggle = (leadId) => {
    setOpenDropDownId((prev) => (prev == leadId ? null : leadId));
  };

  const handleDropClose = () => setOpenDropDownId(false);

  const [openCardId, setOpenCardId] = useState(null);

  const handleAccordionToggle = (leadId) => {
    setOpenCardId((prev) => (prev === leadId ? null : leadId));
  };



  return (
    <div
      className={`h-full flex flex-col relative gap-y-2 ${
        !isLast ? "border-r-2 border-[#E1E4EA] pr-6 " : "mr-6"
      } `}
    >
      {/* Column Header */}

      <motion.div
        className={`rounded-[10px] p-4 border border-[#E1E4EA] bg-[#fff] shadow sticky z-[49] top-0 `}
      >
        <div className="flex flex-col items-center gap-5 self-stretch w-full">
          <div className="flex items-center justify-between self-stretch px-[3px]">
            <div className="flex items-center gap-3">
              {isLoading ? (
                <Skeleton className="h-6 w-3/4 mb-2" />
              ) : (
                <div className="flex gap-1 items-center">
                  <span
                    className={`w-3 h-3 rounded bg-[${
                      colors?.color ? colors.color : "#9D5FEF"
                    }] `}
                    style={
                      colors?.color
                        ? { backgroundColor: colors?.color }
                        : { backgroundColor: "#9D5FEF" }
                    }
                  ></span>
                  <h3 className={`font-bold text-sm uppercase tracking-wider`}>
                    {stage.name}
                  </h3>
                </div>
              )}
            </div>

            <div className="text-[#0E121B] text-[18px] leading-5 font-semibold">
              {counts[`${stage.id}`]}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Cards Area */}
      <div className="flex p-[10px]  mt-2 flex-col items-center gap-5 bg-[#E1E4EA] rounded-xl   h-full overflow-auto">
        <div
          className={`space-y-3 min-h-[200px] overflow-y-hidden  overflow-x-hidden scrollbar-thin   scrollbar-thumb-slate-300  scrollbar-track-transparent h-full w-full`}
        >
          {isLoading
            ? Array(3)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
                  >
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-3" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))
            : sortedLeads.map((lead) => {
                return (
                  <>
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onClick={onLeadClick}
                      colors={colors}
                      onSuccess={onSuccess}
                      onError={onError}
                      onDropdownToggle={() => handleDropDownToggle(lead.id)}
                      isActiveDrop={openDropDownId == lead.id}
                      isOpen={openCardId === lead.id}
                      onToggle={() => handleAccordionToggle(lead.id)}
                      closeDrop={handleDropClose}
                      productionUsers={productionUsers}
                      getUsers={() => {
                        getUsers();
                      }}
                    />
                  </>
                );
              })}
          {counts[`${stage.id}`] > leads?.length && cardLoad ? (
            <div className="">
              <InnerLoader
                textStatus={false}
                className={"!w-5 !h-5 !border-[3px]"}
              />
            </div>
          ) : null}
          {!isLoading && leads.length === 0 && (
            <div className="text-center py-6 text-slate-400 font-medium">
              No projects in this stage
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
